import { useCallback, useEffect, useState } from 'react';
import './Toast.css';

/**
 * Toast — feedback flotante para resultado de operaciones (guardado, borrado, etc.).
 *
 * Uso:
 *   const { toast, showSuccess, showError } = useToast();
 *   ...
 *   showError(t('admin.userSaveError'));
 *   ...
 *   <Toast toast={toast} />
 *
 * Regla de UX del proyecto: ningún error de guardado puede ser silencioso — toda mutación
 * debe mostrar un mensaje amigable en su onError (además del rollback optimista que aplique).
 */
export interface ToastMessage {
  type: 'success' | 'error';
  text: string;
}

const TOAST_AUTO_DISMISS_MS = 4000;

export function useToast(autoDismissMs: number = TOAST_AUTO_DISMISS_MS) {
  const [toast, setToast] = useState<ToastMessage | null>(null);

  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(() => setToast(null), autoDismissMs);
    return () => clearTimeout(timer);
  }, [toast, autoDismissMs]);

  const showSuccess = useCallback((text: string) => setToast({ type: 'success', text }), []);
  const showError   = useCallback((text: string) => setToast({ type: 'error', text }), []);
  const dismiss     = useCallback(() => setToast(null), []);

  return { toast, showSuccess, showError, dismiss };
}

export function Toast({ toast }: { toast: ToastMessage | null }) {
  if (!toast) return null;
  return (
    <div className={`app-toast ${toast.type}`} role="status" aria-live="polite">
      <i className={`fas ${toast.type === 'success' ? 'fa-check-circle' : 'fa-exclamation-circle'}`} />
      <span>{toast.text}</span>
    </div>
  );
}
